// Store slug rules — docs/plans/marketplace-commerce.md §3.1 (`spaces.slug` = the Space actor's handle) and the
// identity plan §6/§19 (reserved words, per-kind handle namespaces). Shared by the browser and the server.
import { RESERVED_HANDLES } from "../identity/handles";

export const STORE_SLUG_MIN_LENGTH = 3;
export const STORE_SLUG_MAX_LENGTH = 40;

/**
 * The database rule is `^[a-z0-9_-]{3,40}$`; a store URL additionally starts and ends with a letter or number
 * and never doubles its separators, so `/store/-a--b_` can't be registered.
 */
export const STORE_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9]|[_-](?=[a-z0-9])){2,39}$/;

/** Hyphenated forms of reserved words: store slugs allow `-`, person handles don't. */
const RESERVED_SLUG_VARIANTS = ["sign-in", "sign-up", "log-in", "log-out", "my-store", "new-store", "studside-store"];

export const RESERVED_STORE_SLUGS: ReadonlySet<string> = new Set([...RESERVED_HANDLES, ...RESERVED_SLUG_VARIANTS]);

export type StoreSlugStatus = "invalid" | "reserved" | "taken" | "available";

export function normalizeStoreSlug(value: string): string {
  return value.trim().toLowerCase();
}

export function storeSlugStatus(value: string, taken: boolean): StoreSlugStatus {
  if (!STORE_SLUG_PATTERN.test(value)) return "invalid";
  if (RESERVED_STORE_SLUGS.has(value)) return "reserved";
  return taken ? "taken" : "available";
}

/** Keeps what the user types URL-shaped while they type (letters, numbers, `-` and `_`). */
export function sanitizeStoreSlugInput(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9_-]/g, "")
    .replace(/([_-])[_-]+/g, "$1")
    .replace(/^[_-]+/, "")
    .slice(0, STORE_SLUG_MAX_LENGTH);
}

/** A slug suggestion from a store name, e.g. "Pixel Ryan's Workshop" → "pixel-ryans-workshop". */
export function suggestStoreSlug(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, STORE_SLUG_MAX_LENGTH)
    .replace(/-+$/, "");
  return slug;
}
